import { type FormEvent, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { supabase, useAuth } from "../lib/auth.tsx";

export function Login() {
  return (
    <AuthCard
      title="Welcome back"
      lead="Sign in to your agents."
      mode="login"
    />
  );
}

export function Signup() {
  return (
    <AuthCard
      title="Create your account"
      lead="Next you’ll chat with a sample cafe, then set up an agent for your own files."
      mode="signup"
    />
  );
}

function AuthCard({ title, lead, mode }: { title: string; lead: string; mode: "login" | "signup" }) {
  const { session } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState(false);

  if (session) return <Navigate to="/app" replace />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!supabase) return;
    setError("");
    setNotice("");
    setPending(true);
    const credentials = { email: email.trim(), password };
    const result =
      mode === "login" ? await supabase.auth.signInWithPassword(credentials) : await supabase.auth.signUp(credentials);
    setPending(false);
    if (result.error) {
      setError(result.error.message === "Failed to fetch" ? "Could not reach Supabase. Check that it is running and that VITE_SUPABASE_URL is correct." : result.error.message);
      return;
    }
    if (mode === "signup" && !result.data.session) {
      setNotice("Check your email to confirm the account, then sign in.");
    }
  }

  return (
    <main className="gate">
      <section className="gate-story">
        <p className="mark">Bubble</p>
        <h1>An agent that only knows your files.</h1>
        <p>Try a sample cafe, then add your own files. Visitors get answers with sources, and nothing those files do not support.</p>
      </section>
      <section className="gate-panel">
        <form className="card" onSubmit={onSubmit}>
          <p className="eyebrow">{mode === "login" ? "Sign in" : "Create account"}</p>
          <h2>{title}</h2>
          <p className="lead">{lead}</p>
          <label>
            Email
            <input type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
          </label>
          <label>
            Password
            <input
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          {error ? <p className="banner bad">{error}</p> : null}
          {notice ? <p className="banner good">{notice}</p> : null}
          <button className="button" type="submit" disabled={pending}>
            {pending ? "Working…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
          {mode === "login" ? (
            <p className="switch">
              New here? <Link to="/signup">Create an account</Link>
            </p>
          ) : (
            <p className="switch">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          )}
        </form>
      </section>
    </main>
  );
}
